import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  reportsApi,
  TicketsReportFilters,
  ContractsReportFilters,
  MonthlyConsumptionReportFilters,
} from '@/lib/api/reports';

export function useReports() {
  const { t } = useTranslation();
  const [isGenerating, setIsGenerating] = useState(false);

  const downloadFile = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const generateTicketsReport = useCallback(
    async (filters: TicketsReportFilters) => {
      try {
        if (!filters.startDate || !filters.endDate || !filters.contractId) return;

        setIsGenerating(true);

        const blob = await reportsApi.generateTicketsReport(filters);
        const filename = `tickets-report-${new Date().toISOString().split('T')[0]}.pdf`;
        downloadFile(blob, filename);
        toast.success(t('reports.tickets.generateSuccess'));
        return true;
      } catch (err: any) {
        toast.error(err.response?.data?.message || t('reports.tickets.generateError'));
        return false;
      } finally {
        setIsGenerating(false);
      }
    },
    [t]
  );

  const generateContractsReport = useCallback(
    async (filters: ContractsReportFilters) => {
      try {
        setIsGenerating(true);
        const blob = await reportsApi.generateContractsReport(filters);
        const filename = `contracts-report-${new Date().toISOString().split('T')[0]}.pdf`;
        downloadFile(blob, filename);
        toast.success(t('reports.contracts.generateSuccess'));
        return true;
      } catch (err: any) {
        toast.error(err.response?.data?.message || t('reports.contracts.generateError'));
        return false;
      } finally {
        setIsGenerating(false);
      }
    },
    [t]
  );

  const generateMonthlyConsumptionReport = useCallback(
    async (filters: MonthlyConsumptionReportFilters) => {
      try {
        setIsGenerating(true);
        const blob = await reportsApi.generateMonthlyConsumptionReport(filters);
        const filename = `monthly-consumption-report-${new Date().toISOString().split('T')[0]}.pdf`;
        downloadFile(blob, filename);
        toast.success(t('reports.monthlyConsumption.generateSuccess'));
        return true;
      } catch (err: any) {
        toast.error(err.response?.data?.message || t('reports.monthlyConsumption.generateError'));
        return false;
      } finally {
        setIsGenerating(false);
      }
    },
    [t]
  );

  return {
    isGenerating,
    generateTicketsReport,
    generateContractsReport,
    generateMonthlyConsumptionReport,
  };
}
